import React from 'react';
import _ from 'underscore';

import {getPurposesForFolderSection} from 'archive/ArchivePurposesUtil';
import BenchlingConfig from 'BenchlingConfig';
import {
  RequestSchema,
  SCHEMA_ID_REQUEST_SCHEMA_ALL,
  SCHEMA_OPTION_REQUEST_SCHEMA_ALL,
} from 'requests/RequestConstants';
import {Schema} from 'schemas/SchemaConstants';
import {ClearSearchButtonProps} from 'search/ClearSearchButton';
import {FilterOption} from 'search/filters/base/FilterComponents';
import {useSecondaryDropdownState} from 'search/filters/FiltersContainer';
import ListingFiltersContainer from 'search/filters/ListingFiltersContainer';
import {getRequestTaskSchemasForRequestSchema} from 'search/filters/RequestTaskSchemaFilterForm';
import {SaveSearchButtonProps} from 'search/saved-searches/SaveSearchButton';
import {Filter, FilterErrors, FilterKey} from 'search/SearchConstants';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';

interface RequestsFiltersContainerProps {
  allTaskSchemas: Array<RequestTaskSchemaGraphQL> | null;
  assaySchemas: Array<RequestSchema> | null;
  filters: Array<Filter>;
  filterErrors: FilterErrors;
  isExpanded: boolean;
  onChangeFilters: (filters: Array<Filter>) => void;
  selectedAssaySchema: RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null;
  saveSearchButton?: React.ReactElement<SaveSearchButtonProps>;
  clearButton?: React.ReactElement<ClearSearchButtonProps> | null;
}

const RequestsFiltersContainer: React.FC<RequestsFiltersContainerProps> = (
  props: RequestsFiltersContainerProps
): JSX.Element => {
  const secondaryDropdownState = useSecondaryDropdownState();
  const {allTaskSchemas, selectedAssaySchema} = props;
  const requestTaskSchemas = getRequestTaskSchemasForRequestSchema(allTaskSchemas || [], selectedAssaySchema);
  const requestorFilterKey = BenchlingConfig.REQUESTS_ENABLE_REQUESTOR
    ? {filterKey: FilterKey.REQUEST_REQUESTOR}
    : {filterKey: FilterKey.CREATOR};

  return (
    <ListingFiltersContainer
      saveSearchButton={props.saveSearchButton}
      primaryFilterOptions={
        _.compact([
          {filterKey: FilterKey.ASSAY_SCHEMA, extraProps: {assaySchemas: props.assaySchemas || []}},
          requestTaskSchemas.length > 0 && {
            filterKey: FilterKey.REQUEST_TASK_SCHEMA,
            extraProps: {selectedAssaySchema},
          },
          props.isExpanded && {filterKey: FilterKey.REQUEST_STATUS},
          props.isExpanded && requestorFilterKey,
          props.isExpanded && {filterKey: FilterKey.REQUEST_ASSIGNEE},
          props.isExpanded && {filterKey: FilterKey.REQUEST_IDS},
        ]) as Array<FilterOption>
      }
      secondaryFilterProps={{
        filterGroups: [
          {
            filterOptions: _.compact([
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {archivePurposes: getPurposesForFolderSection(null)},
              },
              {filterKey: FilterKey.CREATED_AT},
              !props.isExpanded && {filterKey: FilterKey.REQUEST_STATUS},
              !props.isExpanded && requestorFilterKey,
              !props.isExpanded && {filterKey: FilterKey.REQUEST_ASSIGNEE},
              !props.isExpanded && {filterKey: FilterKey.REQUEST_IDS},
              selectedAssaySchema && selectedAssaySchema.id !== SCHEMA_ID_REQUEST_SCHEMA_ALL
                ? {
                    filterKey: FilterKey.SCHEMA_FIELD,
                    extraProps: {
                      schemaFields: (selectedAssaySchema as RequestSchema).fields,
                    },
                  }
                : null,
            ]) as Array<FilterOption>,
          },
        ],
        ...secondaryDropdownState,
      }}
      filters={props.filters}
      filterErrors={props.filterErrors}
      onChangeFilters={props.onChangeFilters}
      selectedSchema={
        selectedAssaySchema !== SCHEMA_OPTION_REQUEST_SCHEMA_ALL
          ? (selectedAssaySchema as Schema | null)
          : null
      }
      clearButton={props.clearButton}
    />
  );
};

export default RequestsFiltersContainer;
